import type { OnInit } from "@angular/core";
import { Component, computed, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import type { FormControl, FormGroup } from "@angular/forms";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoPipe } from "@jsverse/transloco";
import type { Observable } from "rxjs";
import { timer } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { NgOptimizedImage } from "@angular/common";
import { DirectusTranslationPipe } from "../../../common/pipes/directus-translation.pipe";
import { GetDirectusImagePipe } from "../../../common/pipes/get-directus-image.pipe";
import { PathFromItemPipe } from "../../../common/pipes/path-from-item.pipe";
import { sanitizeDirectusObject, sanitizeDirectusRelationArray } from "../../../common/utils/sanitize-directus-object";
import { FooterService } from "./store/footer.service";
import { FooterStore } from "./store/footer.store";

@Component({
    selector: "packbo-footer",
    templateUrl: "./footer.template.html",
    styleUrls: ["./footer.style.css"],
    standalone: true,
    providers: [FooterService, FooterStore],
    imports: [
        GetDirectusImagePipe,
        DirectusTranslationPipe,
        PathFromItemPipe,
        TranslocoPipe,
        ReactiveFormsModule,
        NgOptimizedImage
    ]
})
export class FooterComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly footerService = inject(FooterService);
    private readonly footerStore = inject(FooterStore);
    private readonly formBuilder = inject(FormBuilder);

    public submitMessage = this.footerStore.submitMessage;
    public footerDefinition = this.footerStore.footerDefinition;
    public copyrightDefinition = this.footerStore.copyrightDefinition;
    public footerNavigationItems = computed(() =>
        sanitizeDirectusRelationArray(this.footerDefinition()?.navigation_items_refs, "navigation_items_id").map(
            sanitizeDirectusObject
        )
    );
    public footerSocialLinks = computed(() =>
        sanitizeDirectusRelationArray(this.footerDefinition()?.social_links_refs, "social_links_id").map(
            sanitizeDirectusObject
        )
    );
    public copyrightNavigationItems = computed(() =>
        sanitizeDirectusRelationArray(this.copyrightDefinition()?.navigation_items_refs, "navigation_items_id").map(
            sanitizeDirectusObject
        )
    );

    public newsletterForm: FormGroup<{ email: FormControl<string | null> }>;
    public isSubmitting = false;

    constructor() {
        this.newsletterForm = this.formBuilder.group({
            email: ["", [Validators.required, Validators.email]]
        });
    }

    public ngOnInit(): void {
        this.footerService.loadFooterContent().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    public onNewsletterSubmit(): void {
        if (this.newsletterForm.valid && !this.isSubmitting) {
            this.isSubmitting = true;

            const email = this.newsletterForm.get("email")?.value ?? "";

            this.footerService
                .createNewsletterSubscription(email)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    catchError(() => {
                        this.isSubmitting = false;
                        this.footerService.setSubmitMessage("footer.newsletter_error");
                        return this.clearMessageAfterDelay();
                    }),
                    switchMap(() => {
                        this.isSubmitting = false;
                        this.footerService.setSubmitMessage("footer.newsletter_success");
                        this.newsletterForm.reset();
                        return this.clearMessageAfterDelay();
                    })
                )
                .subscribe();
        }
    }

    private clearMessageAfterDelay(): Observable<void> {
        return timer(6000).pipe(
            map(() => {
                this.footerService.setSubmitMessage(null);
            })
        );
    }
}
